'use client';

import cn from 'classnames';
import { InputHTMLAttributes, ReactNode, forwardRef, useId } from 'react';
import { Text } from '../Text';

export interface CheckboxProps
  extends Omit<InputHTMLAttributes<HTMLInputElement>, 'id' | 'type'> {
  label?: ReactNode;
  errorMessage?: string;
  wrapperClassName?: string;
}

export const Checkbox = forwardRef<HTMLInputElement, CheckboxProps>(
  ({ label, errorMessage, wrapperClassName, className, ...inputProps }, ref) => {
    const id = useId();

    return (
      <div className={cn('flex flex-col gap-spacing-4', wrapperClassName)}>
        <label
          htmlFor={id}
          className="flex items-start gap-spacing-8 cursor-pointer"
        >
          <span
            className="relative inline-flex flex-shrink-0 w-2 h-2 my-auto"
          >
            <input
              ref={ref}
              id={id}
              type="checkbox"
              className={cn(
                'peer h-full w-full cursor-pointer rounded-[3px]',
                'appearance-none',
                'icon-rating bg-transparent border-solid border-gray-200 border-[1px]',
                'checked:bg-current checked:border-current',
                'focus-visible:outline focus-visible:outline-2 focus-visible:outline-current focus-visible:outline-offset-2',
                'transition-colors',
                className,
              )}
              {...inputProps}
            />
            <svg
              aria-hidden
              viewBox="0 0 16 16"
              className="pointer-events-none absolute inset-0 m-auto h-[10px] w-[10px] hidden peer-checked:block text-white"
            >
              <path
                d="M3 8 L6.5 11.5 L13 4.5"
                fill="none"
                stroke="currentColor"
                strokeWidth="2.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </span>
          {label && (
            <Text as="span" type="caption2" className="text-primary">
              {label}
            </Text>
          )}
        </label>
        {errorMessage && (
          <Text as="span" type="caption2" className="text-red-500">
            {errorMessage}
          </Text>
        )}
      </div>
    );
  },
);

Checkbox.displayName = 'Checkbox';

export default Checkbox;
